/**
 * DictationModal — Globales Diktat-Overlay für eine ganze Akte.
 *
 * Workflow:
 *   1. User klickt floating Action Button (FAB) → Modal öffnet sich.
 *   2. Großer Record-Button → MediaRecorder läuft (Echtzeit-Timer).
 *   3. Stopp → Audio geht an /api/ai/voice-fill (apply=false).
 *   4. Modal zeigt: Transkript + KI-Zusammenfassung + erkannte Felder
 *      als Checkliste (jedes Feld pre-checked, User kann abwählen).
 *   5. „Übernehmen" → patcht via PATCH /api/cases/{id} die selektierten
 *      Felder direkt in die Akte und triggert ``onApplied()``.
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Square, Loader2, Sparkles, CheckCircle2, RefreshCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const FIELD_LABELS = {
  "customer.name": "Halter — Nachname",
  "customer.firstname": "Halter — Vorname",
  "customer.address": "Halter — Straße",
  "customer.postal_code": "Halter — PLZ",
  "customer.city": "Halter — Ort",
  "customer.phone": "Halter — Telefon",
  "customer.email": "Halter — E-Mail",
  "customer.is_taxable": "Halter — Vorsteuer-berechtigt",
  "customer.opponent_name": "Unfallgegner — Name",
  "customer.opponent_plate": "Unfallgegner — Kennzeichen",
  "vehicle.vin": "Fahrzeug — VIN",
  "vehicle.license_plate": "Fahrzeug — Kennzeichen",
  "vehicle.make": "Fahrzeug — Hersteller",
  "vehicle.model": "Fahrzeug — Modell",
  "vehicle.year": "Fahrzeug — Baujahr",
  "vehicle.first_registration": "Fahrzeug — Erstzulassung",
  "vehicle.mileage": "Fahrzeug — Kilometerstand",
  "vehicle.color": "Fahrzeug — Farbe",
  "vehicle.fuel_type": "Fahrzeug — Kraftstoff",
  "vehicle.power_kw": "Fahrzeug — Leistung (kW)",
  "vehicle.equipment": "Fahrzeug — Ausstattung",
  "vehicle.condition": "Fahrzeug — Zustand",
  "insurance.insurer_name": "Versicherung — Name",
  "insurance.policy_number": "Versicherung — Police-Nr.",
  "insurance.claim_number": "Versicherung — Schaden-Nr.",
  "insurance.coverage_type": "Versicherung — Deckungsart",
  "insurance.claim_date": "Versicherung — Schadendatum",
  "insurance.damage_location": "Versicherung — Schadenort",
  "damage_description": "Schadensbeschreibung",
  "previous_damages": "Vorschäden",
  "appraiser_notes": "Interne Notizen",
};

export default function DictationModal({ open, onOpenChange, caseId, onApplied }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | processing | review | applying
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [fields, setFields] = useState({}); // {path: value}
  const [summary, setSummary] = useState("");
  const [selected, setSelected] = useState({}); // {path: bool}
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      stopTimer();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setPhase("idle");
      setSeconds(0);
      setTranscript("");
      setFields({});
      setSummary("");
      setSelected({});
    }
  }, [open]);

  const startTimer = () => {
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stopTimer();
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setPhase("processing");
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", "de");
          if (caseId) fd.append("case_id", caseId);
          fd.append("apply", "false");
          const { data } = await api.post("/ai/voice-fill", fd);
          setTranscript(data.transcript || "");
          setFields(data.fields || {});
          setSummary(data.summary || "");
          // pre-select all fields
          const sel = {};
          Object.keys(data.fields || {}).forEach((k) => { sel[k] = true; });
          setSelected(sel);
          setPhase("review");
        } catch (e) {
          toast.error(apiError(e, "Diktat konnte nicht verarbeitet werden"));
          setPhase("idle");
        }
      };
      rec.start();
      setPhase("recording");
      startTimer();
    } catch {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const applySelected = async () => {
    const chosen = Object.entries(selected).filter(([, ok]) => ok).map(([k]) => k);
    if (!chosen.length || !caseId) {
      toast.info("Keine Felder ausgewählt");
      return;
    }
    setPhase("applying");
    try {
      // Build nested patch object from dot-paths
      const patch = {};
      chosen.forEach((path) => {
        const value = fields[path];
        if (value === undefined || value === null || value === "") return;
        if (!path.includes(".")) {
          patch[path] = value;
        } else {
          const [top, sub] = path.split(".", 2);
          patch[top] = patch[top] || {};
          patch[top][sub] = value;
        }
      });
      await api.patch(`/cases/${caseId}`, patch);
      toast.success(`${chosen.length} Felder übernommen`);
      onApplied?.(patch);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Übernahme fehlgeschlagen"));
      setPhase("review");
    }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dictation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-600" />
            KI-Diktat
          </DialogTitle>
          <DialogDescription>
            Sprechen Sie frei — die KI ordnet das Gesprochene automatisch den passenden Akten-Feldern zu.
          </DialogDescription>
        </DialogHeader>

        {/* PHASE: idle */}
        {phase === "idle" && (
          <div className="py-6 flex flex-col items-center gap-4">
            <button
              type="button"
              onClick={startRecording}
              className="h-24 w-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform"
              data-testid="dictation-start-btn"
            >
              <Mic className="h-10 w-10" />
            </button>
            <p className="text-sm text-zinc-600 text-center max-w-md">
              Beispiel: <em>„Halter ist Hans Müller, Musterstraße 12 in 10115 Berlin. Fahrzeug BMW 3er, Kennzeichen B-MM 1234, 156.000 km. Haftpflichtschaden, hintere rechte Stoßstange eingedrückt."</em>
            </p>
          </div>
        )}

        {/* PHASE: recording */}
        {phase === "recording" && (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={stopRecording}
                className="h-24 w-24 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-lg animate-pulse"
                data-testid="dictation-stop-btn"
              >
                <Square className="h-10 w-10" />
              </button>
              <div className="absolute -inset-2 rounded-full border-2 border-rose-300 animate-ping pointer-events-none" />
            </div>
            <div className="text-2xl font-mono font-bold text-rose-700">{fmtTime(seconds)}</div>
            <p className="text-xs text-zinc-500">Sprich frei. Klicke auf den Stop-Button um zu beenden.</p>
          </div>
        )}

        {/* PHASE: processing */}
        {phase === "processing" && (
          <div className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-cyan-600" />
            <p className="text-sm text-zinc-600">Transkribiere und analysiere…</p>
          </div>
        )}

        {/* PHASE: review */}
        {phase === "review" && (
          <div className="space-y-4" data-testid="dictation-review">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Transkript</label>
              <Textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={3}
                className="text-sm"
                data-testid="dictation-transcript"
              />
            </div>
            {summary && (
              <div className="rounded-lg bg-cyan-50 border border-cyan-200 px-3 py-2 text-xs text-cyan-900">
                <strong>KI-Zusammenfassung:</strong> {summary}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Erkannte Felder ({Object.keys(fields).length})</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSelected(Object.fromEntries(Object.keys(fields).map((k) => [k, true])))}
                    className="text-[11px] underline text-cyan-700">Alle</button>
                  <button type="button" onClick={() => setSelected({})}
                    className="text-[11px] underline text-zinc-500">Keine</button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                {Object.entries(fields).length === 0 && (
                  <div className="p-4 text-sm text-zinc-500 italic">Keine Felder erkannt — bitte erneut sprechen mit konkreteren Angaben.</div>
                )}
                {Object.entries(fields).map(([path, value]) => (
                  <label key={path} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50"
                    data-testid={`dictation-field-${path}`}>
                    <Checkbox checked={!!selected[path]}
                      onCheckedChange={(v) => setSelected((s) => ({ ...s, [path]: !!v }))}
                      className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{FIELD_LABELS[path] || path}</div>
                      <div className="text-sm text-zinc-900 break-words">{String(value)}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2 flex-wrap">
              <Button type="button" variant="outline" onClick={() => setPhase("idle")} data-testid="dictation-redo-btn">
                <RefreshCcw className="h-4 w-4 mr-2" />
                Neu aufnehmen
              </Button>
              <Button type="button" onClick={applySelected}
                disabled={Object.values(selected).every((v) => !v) || !caseId}
                className="ml-auto brand-gradient-bg text-white"
                data-testid="dictation-apply-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                In Akte übernehmen
              </Button>
            </div>
          </div>
        )}

        {phase === "applying" && (
          <div className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-cyan-600" />
            <p className="text-sm text-zinc-600">Felder werden gespeichert…</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
